import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Wand2, Gift, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { RecordingTips } from '../components/get-started/RecordingTips';
import { VideoUploader } from '../components/get-started/VideoUploader';
import { CharacterSelector } from '../components/get-started/CharacterSelector';
import { OrderReview } from '../components/get-started/OrderReview';
import { OrderConfirmation } from '../components/get-started/OrderConfirmation';
import { ProgressSteps } from '../components/get-started/ProgressSteps';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { createOrder } from '../services/orders/orderService';

export const GetStarted: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedVideo, setUploadedVideo] = useState<File | null>(null);
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const handleVideoUpload = (file: File) => {
    setUploadedVideo(file);
    setCurrentStep(2);
  };

  const handleCharacterSelect = (characterId: string) => {
    setSelectedCharacter(characterId);
    setCurrentStep(3);
  };

  const handleConfirm = async () => {
    if (!user) {
      navigate('/login', { 
        state: { 
          from: '/get-started',
          videoData: {
            video: uploadedVideo,
            character: selectedCharacter
          }
        }
      });
      return;
    }

    if (!uploadedVideo || !selectedCharacter) {
      setError('Please complete all required fields');
      return;
    }

    try {
      const newOrderId = await createOrder(
        user.uid,
        user.email || '',
        selectedCharacter,
        uploadedVideo
      );
      setOrderId(newOrderId);
      setCurrentStep(4);
    } catch (error) {
      setError('Failed to create order. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-red-50 to-white pt-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">
            Let's Create Your Magical Holiday Video! 🎅✨
          </h1>
          <p className="text-xl text-gray-600">
            Follow these steps to create your personalized Santa video.
          </p>
        </motion.div>

        {/* Progress Steps */}
        <ProgressSteps currentStep={currentStep} />

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-center"
          >
            <AlertCircle className="w-5 h-5 mr-2" />
            <p>{error}</p>
          </motion.div>
        )}

        {/* Main Content */}
        <div className="mt-12 space-y-12">
          {currentStep < 4 ? (
            <>
              {/* Step 1: Recording Tips */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: currentStep === 1 ? 1 : 0.5 }}
                className="bg-white rounded-2xl p-8 shadow-xl"
              >
                <RecordingTips />
                <VideoUploader onUpload={handleVideoUpload} />
              </motion.div>

              {/* Step 2: Character Selection */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: currentStep === 2 ? 1 : 0.5 }}
                className={`bg-white rounded-2xl p-8 shadow-xl ${
                  currentStep < 2 ? 'pointer-events-none' : ''
                }`}
              >
                <CharacterSelector
                  selectedCharacter={selectedCharacter}
                  onSelect={handleCharacterSelect}
                />
              </motion.div>

              {/* Step 3: Review */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: currentStep === 3 ? 1 : 0.5 }}
                className={`bg-white rounded-2xl p-8 shadow-xl ${
                  currentStep < 3 ? 'pointer-events-none' : ''
                }`}
              >
                <OrderReview
                  video={uploadedVideo}
                  character={selectedCharacter}
                  onConfirm={handleConfirm}
                />
              </motion.div>
            </>
          ) : (
            /* Step 4: Confirmation */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              {orderId && <OrderConfirmation orderId={orderId} />}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};